/// <amd-module name="Artifacts/FeedSettings/components/PermissionsTab" />
import { AddPermissionPanel } from "Artifacts/FeedSettings/components/AddPermissionPanel";
import { setFocusOnColumnMore } from "Artifacts/FeedSettings/helpers/FeedSettingsHelpers";
import * as Res from "Artifacts/FeedSettings/Resources";
import { DefaultLoader } from "Artifacts/Root/components/DefaultLoader";
import { ComponentState } from "Artifacts/Root/Constants";
import { roleToLocaleString } from "Artifacts/Root/helpers/RoleHelpers";
import { resizeColumn } from "Artifacts/Root/helpers/TableHelper";
import { FeedPermission } from "Artifacts/Root/web-apis/contracts/Feed";
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { Card } from "VSSUI/Card";
import { Icon } from "VSSUI/Icon";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import {
  ColumnMore,
  ISimpleTableCell,
  ITableColumn,
  renderSimpleCell,
  SimpleTableCell,
  Table,
} from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export interface IPermissionsTabProps {
  componentState: ComponentState;
  permissions?: FeedPermission[];
  addPermission: (permission: FeedPermission) => void;
  addPermissionComponentState: ComponentState;
  showDeleteModal: (identityIdToDelete: string, setFocus: () => void) => void;
  setShowAddPermissionPanel: (value: boolean) => void;
  showAddPermissionPanel: boolean;
  isFeedDeleted: boolean;
  isPublicFeed: boolean;
  isUserAdmin: boolean;
  supportServicePrincipals: boolean;
}

interface IPermissionTableItem {
  user: string;
  role: string;
  inherited: boolean;
  identityId: string;
}

const tableRef = React.createRef<Table<any>>();
let columns: ITableColumn<IPermissionTableItem | ISimpleTableCell>[] = [
  {
    id: "user",
    name: Res.FeedSettings_Permissions_Grid_UserGroup,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-100),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  },
  {
    id: "role",
    name: Res.FeedSettings_Permissions_Grid_Role,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-70),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  },
  {
    id: "inherited",
    name: Res.FeedSettings_Permissions_Grid_Inheritance,
    readonly: true,
    renderCell: renderInherited,
    width: new ObservableValue(-70),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  },
];

export const PermissionsTab: React.FC<IPermissionsTabProps> = ({
  permissions,
  componentState,
  addPermission,
  addPermissionComponentState,
  showDeleteModal,
  setShowAddPermissionPanel,
  showAddPermissionPanel,
  isFeedDeleted,
  isPublicFeed,
  isUserAdmin,
  supportServicePrincipals
}) => {
  const tableColumns = [...columns];
  !isFeedDeleted &&
    tableColumns.push(
      new ColumnMore((item, rowIndex) => ({
        id: "sub-menu",
        items: [
          {
            id: "delete",
            onActivate: () => {
              showDeleteModal((item as IPermissionTableItem).identityId, () =>
                setFocusOnColumnMore(tableRef, rowIndex),
              );
            },
            text: Res.Delete_Label,
            disabled:
              permissionsItemProvider.length <= 1 ||
              permissions?.find((p) => p.identityId === (item as IPermissionTableItem).identityId)
                ?.isInheritedRole,
          },
        ],
      })),
    );

  const permissionsItemProvider = new ArrayItemProvider(
    permissions?.map<IPermissionTableItem>((permission) => ({
      inherited: permission.isInheritedRole,
      role: roleToLocaleString(permission.role),
      user: permission.displayName,
      identityId: permission.identityId,
    })) || [],
  );

  return (
    <>
      <DefaultLoader state={componentState}>
        {isPublicFeed && (
          <div className="public-feed-message">
            <MessageCard severity={MessageCardSeverity.Info}>
              {Res.FeedSettings_Permissions_PublicFeedPermissionMessage}
            </MessageCard>
          </div>
        )}
        <Card
          className="flex-grow bolt-table-card"
          contentProps={{ contentPadding: false }}
          titleProps={{ text: Res.FeedSettings_Permissions_Title, ariaLevel: 2 }}
          headerCommandBarItems={
            !isFeedDeleted
              ? [
                  {
                    id: "add",
                    text: Res.FeedSettings_Permissions_Grid_AddPermission_Label,
                    isPrimary: true,
                    onActivate: () => {
                      setShowAddPermissionPanel(true);
                    },
                    disabled: !isUserAdmin,
                  },
                ]
              : undefined
          }
        >
          <Table
            columns={tableColumns}
            itemProvider={permissionsItemProvider}
            rowHeight={100}
            ref={tableRef}
          />
        </Card>
        {showAddPermissionPanel && (
          <AddPermissionPanel
            onSave={(permission) => {
              if (permission) {
                addPermission(permission);
              }
            }}
            onDismiss={() => {
              setShowAddPermissionPanel(false);
            }}
            saveButtonComponentState={addPermissionComponentState}
            supportServicePrincipals={supportServicePrincipals}
          />
        )}
      </DefaultLoader>
    </>
  );
};

function renderInherited(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IPermissionTableItem>,
  tableItem: IPermissionTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      {tableItem.inherited && <Icon ariaLabel="Check mark" iconName="CheckMark" />}
    </SimpleTableCell>
  );
}
